import React, {useState, useRef, useEffect} from 'react';
import GoogleMapReact, {Bounds, ChangeEventValue} from 'google-map-react';
import {Location} from "../../types/location";
import {LocationMarker} from "./LocationMarker";
import useSupercluster from "use-supercluster";
import {PointFeature} from "supercluster";
import {LocationCluster} from "./LocationCluster";
import {getBoundsForCoords} from "../../utility/googleMapsUtility";
import {Mode} from "../../enum/mode";


type GoogleMapProps = {
    locations: Location[],
    onBoundChange: (bound : Bounds) => void,
    selectedLocation: Location|null,
    mapHoverLocation: Location|null,
    setSelectedLocation: (location : Location|null) => void
    setMapHoverLocation: (location : Location|null) => void
    mode: Mode
}

type LocationClusterPoint = {
    cluster: boolean,
    cluster_id?: number,
    point_count?: number,
    point_count_abbreviated?: string | number,
    location: Location
}

export function GoogleMap({locations, onBoundChange, selectedLocation, mapHoverLocation, setSelectedLocation, setMapHoverLocation, mode} : GoogleMapProps) {

    const mapRef = useRef<any>(null);

    const defaultMapValues = {
        center: {
            lat: 47.368650,
            lng: 8.539183
        },
        zoom: 9,
        bounds: {
            nw: {lat: 48.65474013198943, lng: 11.972410539062508},
            ne: {lat: 48.65474013198943, lng: 5.105955460937508},
            sw: {lat: 46.05041800652981, lng: 11.972410539062508},
            se: {lat: 46.05041800652981, lng: 5.105955460937508}
        }
    };

    const [bounds, setBounds] = useState<Bounds>(defaultMapValues.bounds);
    const [zoom, setZoom] = useState(defaultMapValues.zoom);

    const points : PointFeature<LocationClusterPoint>[] = locations.map((location) => {
        return {
            type: "Feature",
            id: location.uid,
            properties: {
                cluster: false,
                location: location
            },
            geometry: {
                type: "Point",
                coordinates: [location.lng, location.lat]
            }
        }
    });

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds: [bounds.nw.lng, bounds.se.lng, bounds.se.lng, bounds.nw.lat],
        zoom,
        options: { radius: 90, maxZoom: 16 }
    });

    useEffect(() => {
        if(selectedLocation !== null) {
            mapRef.current.panTo({lat: selectedLocation.lat, lng: selectedLocation.lng});
        }

    }, [selectedLocation])

    useEffect(() => {
        if(mapRef.current !== null && locations.length) {
            const locationCoords = locations.map(location => {return {lat: location.lat, lng: location.lng}});
            const maxZoom = mapRef.current.maxZoom;

            mapRef.current.setOptions({maxZoom: 18}); // assure fitBounds does not zoom to much
            mapRef.current.fitBounds(
                getBoundsForCoords(locationCoords)
            )
            mapRef.current.setOptions({maxZoom: maxZoom});
        }
    }, [locations]);

    const onChange = (event : ChangeEventValue) => {
        setBounds(event.bounds);
        setZoom(event.zoom);

        onBoundChange(event.bounds);
    };

    const zoomIntoCluster = (clusterId : number|undefined, lat : number, lng : number) => {
        if(supercluster && clusterId) {
            const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(clusterId),
                20
            );

            if(mapRef.current) {
                mapRef.current.setZoom(expansionZoom);
                mapRef.current.panTo({ lat: lat, lng: lng });
            }
        }
    }

   const createMapOptions = () => {
        return {
            styles: [{ stylers: [{ 'saturation': -100 }, { 'gamma': 0.8 }]}]
        }
    }

    return (
        <div className="googleMapsContainer" style={{ width: '100%', height: '500px' }}>
            <GoogleMapReact
                bootstrapURLKeys={{key: 'AIzaSyDTJYA37ykD8DrcPYbf2IHFicuF2ynAe84'}}
                defaultCenter={defaultMapValues.center}
                defaultZoom={defaultMapValues.zoom}
                onChange={onChange}
                onClick={() => setSelectedLocation(null)}
                yesIWantToUseGoogleMapApiInternals
                options={createMapOptions}
                onGoogleApiLoaded={({ map }) => {
                    mapRef.current = map;
                }}
            >
                {
                    clusters && clusters.map((locationPoint, index) => {

                        if(locationPoint.properties.cluster) {
                            const [lng, lat] = locationPoint.geometry.coordinates;
                            return (
                                <LocationCluster
                                    lat={lat}
                                    lng={lng}
                                    key={index}
                                    pointCount={locationPoint.properties.point_count || 0}
                                    points={clusters.length}
                                    onClick={() => zoomIntoCluster(locationPoint.properties.cluster_id, lat, lng)}
                                />
                            );
                        } else {
                            const location : Location = locationPoint.properties.location;

                            return (
                                <LocationMarker
                                    location={location}
                                    lat={location.lat}
                                    lng={location.lng}
                                    key={index}
                                    selected={selectedLocation !== null && selectedLocation.uid === location.uid}
                                    hover={mapHoverLocation !== null && mapHoverLocation.uid === location.uid}
                                    setMapHoverLocation={setMapHoverLocation}
                                    setSelectedLocation={setSelectedLocation}
                                    mode={mode}
                                />
                            );
                        }

                    })
                }
            </GoogleMapReact>
        </div>
    );
}
